/**
 * Authentication utilities for handling token expiry and auto-logout
 */

/**
 * Decode JWT token without verification (for checking expiry)
 */
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token) {
  if (!token) return true;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Handle auto-logout for different user types
 */
export function handleAutoLogout() {
  // Determine user type and clear appropriate storage
  const adminAccess = localStorage.getItem('gogig_admin_access');
  const supervisorAccess = localStorage.getItem('gogig_supervisor_access');
  const spocAccess = localStorage.getItem('gogig_spoc_access');
  const companyToken = localStorage.getItem('gogig_company_token');
  const candidateToken = localStorage.getItem('gogig_token');
  const institutionAccess = localStorage.getItem('gogig_institution_access');
  
  // Clear all auth-related storage
  localStorage.removeItem('gogig_admin_access');
  localStorage.removeItem('gogig_admin_refresh');
  localStorage.removeItem('gogig_admin');
  localStorage.removeItem('gogig_supervisor_access');
  localStorage.removeItem('gogig_supervisor_refresh');
  localStorage.removeItem('gogig_supervisor_data');
  localStorage.removeItem('gogig_supervisor');
  localStorage.removeItem('gogig_spoc_access');
  localStorage.removeItem('gogig_spoc_data');
  localStorage.removeItem('gogig_company_token');
  localStorage.removeItem('gogig_company_data');
  localStorage.removeItem('gogig_token');
  localStorage.removeItem('gogig_user');
  localStorage.removeItem('gogig_institution_access');
  localStorage.removeItem('gogig_institution_refresh');
  localStorage.removeItem('gogig_institution_data');
  localStorage.removeItem('gogig_institution');
  localStorage.removeItem('isLoggedIn');
  
  // Show notification
  if (typeof window !== 'undefined' && window.alert) {
    alert('Your session has expired. Please log in again.');
  }
  
  // Redirect to appropriate login page
  const currentPath = window.location.pathname;
  
  if (currentPath.startsWith('/admin')) {
    window.location.href = '/admin';
  } else if (currentPath.startsWith('/supervisor')) {
    window.location.href = '/supervisor/login';
  } else if (currentPath.startsWith('/spoc')) {
    window.location.href = '/spoc/login';
  } else if (currentPath.startsWith('/company')) {
    window.location.href = '/company/login';
  } else if (currentPath.startsWith('/institution')) {
    window.location.href = '/institution/login';
  } else {
    // Default to welcome page
    window.location.href = '/welcome';
  }
}

/**
 * Check token and auto-logout if expired
 */
export function checkTokenAndLogout() {
  // Check admin token
  const adminAccess = localStorage.getItem('gogig_admin_access');
  if (adminAccess && isTokenExpired(adminAccess)) {
    handleAutoLogout();
    return true;
  }
  
  // Check supervisor token
  const supervisorAccess = localStorage.getItem('gogig_supervisor_access');
  if (supervisorAccess && isTokenExpired(supervisorAccess)) {
    handleAutoLogout();
    return true;
  }
  
  // Check SPOC token
  const spocAccess = localStorage.getItem('gogig_spoc_access');
  if (spocAccess && isTokenExpired(spocAccess)) {
    handleAutoLogout();
    return true;
  }
  
  // Check company token
  const companyToken = localStorage.getItem('gogig_company_token');
  if (companyToken && isTokenExpired(companyToken)) {
    handleAutoLogout();
    return true;
  }
  
  // Check candidate token
  const candidateToken = localStorage.getItem('gogig_token');
  if (candidateToken && isTokenExpired(candidateToken)) {
    handleAutoLogout();
    return true;
  }
  
  // Check institution token
  const institutionAccess = localStorage.getItem('gogig_institution_access');
  if (institutionAccess && isTokenExpired(institutionAccess)) {
    handleAutoLogout();
    return true;
  }
  
  return false;
}

/**
 * Enhanced fetch wrapper that handles 401 errors automatically
 */
export async function authenticatedFetch(url, options = {}) {
  // Check token before making request
  if (checkTokenAndLogout()) {
    throw new Error('Session expired');
  }
  
  // Get appropriate token based on URL or current path
  let token = null;
  const path = window.location.pathname;
  
  if (path.startsWith('/admin') || url.includes('/api/admin')) {
    token = localStorage.getItem('gogig_admin_access');
  } else if (path.startsWith('/supervisor') || url.includes('/api/supervisor')) {
    token = localStorage.getItem('gogig_supervisor_access');
  } else if (path.startsWith('/spoc') || url.includes('/api/spoc')) {
    token = localStorage.getItem('gogig_spoc_access');
  } else if (path.startsWith('/company') || url.includes('/api/company')) {
    token = localStorage.getItem('gogig_company_token');
  } else if (path.startsWith('/institution') || url.includes('/api/institution')) {
    token = localStorage.getItem('gogig_institution_access');
  } else {
    token = localStorage.getItem('gogig_token');
  }
  
  // Add authorization header if token exists
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    // Handle 401 Unauthorized - token expired
    if (response.status === 401) {
      handleAutoLogout();
      throw new Error('Session expired');
    }
    
    return response;
  } catch (error) {
    // If it's a network error and we have a token, check if it's expired
    if (token && error.message.includes('Failed to fetch')) {
      // Don't auto-logout on network errors, only on 401
    }
    throw error;
  }
}

/**
 * Set up periodic token checking
 */
export function setupTokenValidation(intervalMs = 60000) {
  // Check immediately
  checkTokenAndLogout();
  
  // Set up interval to check every minute
  const intervalId = setInterval(() => {
    checkTokenAndLogout();
  }, intervalMs);
  
  // Return cleanup function
  return () => clearInterval(intervalId);
}

/**
 * Set up global fetch interceptor for 401 errors
 * This wraps the native fetch to automatically handle token expiry
 */
export function setupFetchInterceptor() {
  const originalFetch = window.fetch;
  
  window.fetch = async function(...args) {
    try {
      const response = await originalFetch.apply(this, args);
      
      // Check for 401 Unauthorized
      if (response.status === 401) {
        // Check if it's a token expiry error
        const clonedResponse = response.clone();
        try {
          const data = await clonedResponse.json();
          if (data.code === 'TOKEN_EXPIRED' || data.error === 'Token expired' || data.message?.includes('expired') || data.message?.includes('Invalid token')) {
            handleAutoLogout();
            throw new Error('Session expired');
          }
        } catch (e) {
          // If JSON parsing fails, still handle 401 as token expiry
          handleAutoLogout();
          throw new Error('Session expired');
        }
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  };
  
  // Return cleanup function to restore original fetch
  return () => {
    window.fetch = originalFetch;
  };
}

